'use client';

import { useState, useCallback } from 'react';
import { getChatCompletion, getStreamingChatCompletion } from '@/lib/ai/chatCompletion';

function toError(err: unknown): Error {
  // Preserve typed credit errors (InsufficientCreditsError, ConcurrentProcessingError, RateLimitError)
  // so consumers can call resolveCreditErrorMessage() / isInsufficientCreditsError().
  if (err instanceof Error) return err;
  return new Error('Unknown error');
}

export interface UseChatOptions {
  caller?: string;
}

export function useChat(
  provider: string,
  model: string,
  streaming: boolean = true,
  options: UseChatOptions = {}
) {
  const [response, setResponse] = useState('');
  const [fullResponse, setFullResponse] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const caller = options.caller?.trim() || 'unknown-legacy-caller';

  const sendMessage = useCallback(
    async (messages: object[], parameters: object = {}) => {
      if (isLoading) return;

      setResponse('');
      setFullResponse(streaming ? [] : null);
      setIsLoading(true);
      setError(null);

      try {
        if (streaming) {
          await getStreamingChatCompletion(
            provider,
            model,
            messages,
            (chunk) => {
              setFullResponse((prev: any[]) => [...prev, chunk]);
              const content = (chunk as { choices?: Array<{ delta?: { content?: string } }> })
                ?.choices?.[0]?.delta?.content;
              if (content) setResponse((prev) => prev + content);
            },
            () => setIsLoading(false),
            (err) => {
              setError(toError(err));
              setIsLoading(false);
            },
            parameters,
            { caller }
          );
        } else {
          const result = await getChatCompletion(provider, model, messages, parameters, {
            caller,
          });
          // Balance already applied once inside getChatCompletion — do not re-apply here.
          setFullResponse(result);
          setResponse(result?.choices?.[0]?.message?.content || '');
          setIsLoading(false);
        }
      } catch (err) {
        setError(toError(err));
        setIsLoading(false);
      }
    },
    [provider, model, streaming, isLoading, caller]
  );

  return { response, fullResponse, isLoading, error, sendMessage };
}
